// localStorage helpers for anonymous-user progress on the public homepage.
// Two keys:
//   kobra_anon_quiz_history — array of {date, level, score_pct, total, correct}
//   kobra_anon_favorites    — array of question_id (numbers)
// All access is wrapped in try/catch since localStorage can be unavailable
// (private mode, quota exceeded, JSON corruption, etc.).

const QUIZ_HISTORY_KEY = 'kobra_anon_quiz_history';
const FAVORITES_KEY = 'kobra_anon_favorites';
const PRACTICE_KEY = 'kobra_anon_practice'; // { date: 'YYYY-MM-DD', count: number }
// Display-only "recent activity" log, kept for BOTH anonymous and logged-in users.
// Unlike QUIZ_HISTORY_KEY it is NEVER imported/cleared at login, so the homepage
// activity box shows recent quizzes for everyone. Authoritative records for
// logged-in users still live server-side; this is purely for the on-device box.
const RECENT_QUIZZES_KEY = 'kobra_recent_quizzes';
// Distinct practice question ids whose answer the user has revealed (device-wide,
// all-time). Length = "practice answers viewed" shown in the activity box.
const PRACTICE_REVEALED_KEY = 'kobra_practice_revealed_ids';
const QUIZ_HISTORY_MAX = 50;
const RECENT_QUIZZES_MAX = 50;
// The homepage activity box shows at most a rolling 7-day history: entries older
// than this are neither counted ("N taken") nor listed.
const RECENT_QUIZZES_MAX_DAYS = 7;
const PRACTICE_REVEALED_MAX = 5000;

function _today() {
    // Bangladesh calendar day, so anonymous daily limits align with the server (which
    // counts per BD day) and merge correctly after login. Falls back to UTC date.
    try {
        return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Dhaka' });
    } catch {
        return new Date().toISOString().slice(0, 10);
    }
}

// Exposed so callers stamp anonymous attempts with the same BD day used for counting.
export function anonymousToday() {
    return _today();
}

function _readArray(key) {
    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function _writeArray(key, arr) {
    try {
        window.localStorage.setItem(key, JSON.stringify(arr));
    } catch {
        // swallow
    }
}

export function getAnonymousQuizHistory() {
    return _readArray(QUIZ_HISTORY_KEY);
}

export function appendAnonymousQuiz(entry) {
    const next = [entry, ..._readArray(QUIZ_HISTORY_KEY)].slice(0, QUIZ_HISTORY_MAX);
    _writeArray(QUIZ_HISTORY_KEY, next);
    return next;
}

export function clearAnonymousQuizHistory() {
    try {
        window.localStorage.removeItem(QUIZ_HISTORY_KEY);
    } catch {
        // swallow
    }
}

export function getAnonymousFavorites() {
    return new Set(_readArray(FAVORITES_KEY).map(Number).filter(Number.isFinite));
}

export function toggleAnonymousFavorite(questionId) {
    const set = getAnonymousFavorites();
    if (set.has(questionId)) {
        set.delete(questionId);
    } else {
        set.add(questionId);
    }
    _writeArray(FAVORITES_KEY, [...set]);
    return set;
}

export function clearAnonymousFavorites() {
    try {
        window.localStorage.removeItem(FAVORITES_KEY);
    } catch {
        // swallow
    }
}

// --- Daily usage counters (anonymous homepage limits) ---

// Number of quick quizzes the anonymous user has completed today.
export function getAnonymousQuizCountToday() {
    const today = _today();
    return _readArray(QUIZ_HISTORY_KEY).filter(e => e && e.date === today).length;
}

// Number of practice questions served to the anonymous user today.
export function getAnonymousPracticeCountToday() {
    try {
        const raw = window.localStorage.getItem(PRACTICE_KEY);
        if (!raw) return 0;
        const obj = JSON.parse(raw);
        return obj && obj.date === _today() ? (obj.count || 0) : 0;
    } catch {
        return 0;
    }
}

// Add to today's practice count (resets implicitly when the date changes).
export function addAnonymousPracticeCount(n) {
    const today = _today();
    const current = getAnonymousPracticeCountToday();
    const next = current + n;
    try {
        window.localStorage.setItem(PRACTICE_KEY, JSON.stringify({ date: today, count: next }));
    } catch {
        // swallow
    }
    return next;
}

// --- Homepage activity box (works for anonymous AND logged-in users) ---

// True when a recent-quiz entry falls inside the rolling RECENT_QUIZZES_MAX_DAYS
// window. Prefers the ISO `ts`, falls back to the `date` day; undated/unparseable
// legacy entries are kept so nothing silently vanishes.
function _withinRecentWindow(entry) {
    if (!entry) return false;
    const raw = entry.ts || entry.date;
    if (!raw) return true;
    const when = new Date(raw).getTime();
    if (Number.isNaN(when)) return true;
    const cutoff = Date.now() - RECENT_QUIZZES_MAX_DAYS * 24 * 60 * 60 * 1000;
    return when >= cutoff;
}

// Recent quick quizzes for the on-device activity box. Display-only; never imported.
// Capped to the last RECENT_QUIZZES_MAX_DAYS days so the box shows at most a week of history.
export function getRecentQuizzes() {
    return _readArray(RECENT_QUIZZES_KEY).filter(_withinRecentWindow);
}

export function appendRecentQuiz(entry) {
    const next = [entry, ..._readArray(RECENT_QUIZZES_KEY)]
        .filter(_withinRecentWindow)
        .slice(0, RECENT_QUIZZES_MAX);
    _writeArray(RECENT_QUIZZES_KEY, next);
    return next;
}

// Count of distinct practice questions whose answer the user has viewed (device-wide).
export function getPracticeRevealedCount() {
    return _readArray(PRACTICE_REVEALED_KEY).length;
}

// Record that a practice question's answer was viewed; returns the new distinct count.
export function addPracticeRevealedId(questionId) {
    const ids = _readArray(PRACTICE_REVEALED_KEY).map(Number).filter(Number.isFinite);
    if (!ids.includes(Number(questionId))) {
        const next = [Number(questionId), ...ids].slice(0, PRACTICE_REVEALED_MAX);
        _writeArray(PRACTICE_REVEALED_KEY, next);
        return next.length;
    }
    return ids.length;
}
